import { FC } from 'react';
import classNames from 'classnames';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClock } from '@fortawesome/pro-regular-svg-icons';
import { Field } from 'elements/Field';
import { useFormatDate } from 'hooks/useFormatDate';
import { timeSlots, toTimeKey, withTimeKey } from './timeSlots';

interface Props {
  id: string;
  // What the time is for, for screen readers.
  label: string;
  value: Date;
  // Called with the chosen time on the same day as `value`.
  onChange: (next: Date) => void;
  onBlur?: () => void;
  className?: string;
}

// A time of day picked from a list of quarter hours that opens under the
// field, written in the app's language ("21:45" in French, "9:45 PM" in
// US English).
export const TimePicker: FC<Props> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  className,
}) => {
  const formatDate = useFormatDate();
  const current = toTimeKey(value);

  // The quarter hours of the day, plus the current time when it falls
  // between two of them (an event made elsewhere at 9:37).
  const slots = timeSlots(value);
  if (!slots.some((slot) => toTimeKey(slot) === current)) {
    slots.push(value);
    slots.sort((a, b) => a.getTime() - b.getTime());
  }

  return (
    <Listbox
      value={current}
      onChange={(key: string) => onChange(withTimeKey(value, key))}
    >
      {({ open }) => (
        <>
          <Field.Root
            focused={open}
            className={classNames(styles.root, className)}
          >
            <ListboxButton
              id={id}
              aria-label={label}
              onBlur={onBlur}
              className={styles.button}
            >
              <span>{formatDate(value, 'time')}</span>
              <FontAwesomeIcon
                icon={faClock}
                className={styles.icon}
                aria-hidden="true"
              />
            </ListboxButton>
          </Field.Root>

          <ListboxOptions
            anchor={{ to: 'bottom end', gap: '6px' }}
            className={styles.options}
          >
            {slots.map((slot) => (
              <ListboxOption
                key={toTimeKey(slot)}
                value={toTimeKey(slot)}
                className={styles.option}
              >
                <span>{formatDate(slot, 'time')}</span>
                <FontAwesomeIcon
                  icon={faCheck}
                  className={styles.check}
                  aria-hidden="true"
                />
              </ListboxOption>
            ))}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};

const styles = {
  root: 'px-0',
  button: classNames(
    'flex w-full cursor-pointer items-center justify-between gap-2.5 rounded-[10px] px-3.5 py-2',
    'text-left focus:outline-none',
  ),
  icon: 'shrink-0 text-[13px] text-ink-primary',
  options: classNames(
    // Headless UI sizes an anchored panel through this variable, so a plain
    // max-height class wouldn't hold.
    'z-[60] w-(--button-width) min-w-36 overflow-y-auto rounded-xl bg-surface-overlay p-1 [--anchor-max-height:16rem]',
    'shadow-lg ring-1 ring-hairline focus:outline-hidden',
  ),
  option: classNames(
    'group flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-sm text-ink-primary',
    'data-focus:bg-accent-tint data-selected:font-semibold data-selected:text-accent-tint-ink',
  ),
  check: 'invisible text-[11px] text-accent-ink group-data-selected:visible',
};
